import { Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { SecretRedactor } from '@gitlab-org/secret-redaction';
import {
  formatSessionExport,
  serializeSessionExport,
  type SessionExportBundle,
} from '@gitlab-lsp/workflow-api';
import { CliInitializationService } from '../../cli_initialization_service';
import { SessionDataService } from '../../sessions';
import { ExitHandler } from '../../utils/exit';
import { UsageError } from '../../utils/usage_error';
import {
  exportBudgetWarning,
  EXPORT_HOST_DETAIL_WARNING,
  EXPORT_UNANCHORED_PATHS_WARNING,
  hasUnanchoredHostPaths,
} from './export_warnings';
import { SessionExportWriter } from './session_export_writer';

export interface ExportOptions {
  sessionId: string;
  /** Resolved `--cwd`; relative `-o` paths are taken against it. */
  cwd: string;
  output?: string;
  outputFormat: 'text' | 'json';
}

// The warning goes to stderr so stdout stays machine-consumable.
@Service({
  dependencies: [
    Logger,
    CliInitializationService,
    SessionDataService,
    SecretRedactor,
    SessionExportWriter,
    ExitHandler,
  ],
  lifetime: ServiceLifetime.Singleton,
})
export class ExportController {
  #logger: Logger;

  #initService: CliInitializationService;

  #sessionDataService: SessionDataService;

  #secretRedactor: SecretRedactor;

  #writer: SessionExportWriter;

  #exitHandler: ExitHandler;

  constructor(
    logger: Logger,
    initService: CliInitializationService,
    sessionDataService: SessionDataService,
    secretRedactor: SecretRedactor,
    writer: SessionExportWriter,
    exitHandler: ExitHandler,
  ) {
    this.#logger = withPrefix(logger, '[ExportController]');
    this.#initService = initService;
    this.#sessionDataService = sessionDataService;
    this.#secretRedactor = secretRedactor;
    this.#writer = writer;
    this.#exitHandler = exitHandler;
  }

  async execute(options: ExportOptions): Promise<void> {
    // Brings up the API the checkpoint query needs, and arms the redactor.
    const { criticalError, workspaceFolder } = await this.#initService.initialize();
    if (criticalError) {
      throw new UsageError(criticalError);
    }

    const { sessionId } = options;
    const source = await this.#sessionDataService.getSessionExportSource(sessionId);
    if (!source) {
      throw new UsageError(
        `No session found with ID "${sessionId}". Run \`duo log list\` or open the session picker in the TUI to find one.`,
      );
    }

    const bundle = serializeSessionExport({
      sessionId,
      chatLog: source.chatLog,
      workspaceRoot: workspaceFolder,
      redactor: this.#secretRedactor,
      title: source.title,
      flow: source.flow,
    });

    const emitToStdout = options.outputFormat === 'json';
    // `json` alone is a pipe, so it writes no file; anything else does.
    const path = emitToStdout && !options.output ? undefined : await this.#write(bundle, options);

    if (emitToStdout) {
      process.stdout.write(formatSessionExport(bundle));
    } else if (path) {
      process.stdout.write(`${path}\n`);
    }

    process.stderr.write(`${EXPORT_HOST_DETAIL_WARNING}\n`);
    if (hasUnanchoredHostPaths(bundle)) {
      process.stderr.write(`${EXPORT_UNANCHORED_PATHS_WARNING}\n`);
    }

    const budgetWarning = exportBudgetWarning(bundle);
    if (budgetWarning) {
      process.stderr.write(`${budgetWarning}\n`);
    }

    this.#logger.info(
      `Exported session ${sessionId} (${bundle.summary.entryCount} entries)${path ? ` to ${path}` : ''}`,
    );

    await this.#exitHandler.exit(0);
  }

  async #write(bundle: SessionExportBundle, options: ExportOptions): Promise<string> {
    try {
      return await this.#writer.write(bundle, { cwd: options.cwd, output: options.output });
    } catch (error) {
      if (error instanceof UsageError) throw error;
      throw new UsageError(
        `Could not write the session export: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }
}
